import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Bell, User, LogOut, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useLocation } from '@tanstack/react-router'
import { authApi, systemApi, removeToken } from '@/lib/api'

export function TopBar() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [warningCount, setWarningCount] = useState(0)
  const [userName, setUserName] = useState('Admin')

  useEffect(() => {
    // Load warning count
    systemApi.getWarningArticle(1).then((data: any) => {
      if (data?.total !== undefined) {
        setWarningCount(data.total)
      }
    }).catch(() => {})

    // Load user info
    const stored = sessionStorage.getItem('userName')
    if (stored) setUserName(stored)
  }, [])

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch (err) {
      // ignore
    }
    removeToken()
    sessionStorage.removeItem('isLoggedIn')
    sessionStorage.removeItem('userName')
    sessionStorage.removeItem('userRole')
    navigate({ to: '/login' })
  }

  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/dashboard') return t('nav.dashboard')
    if (path.startsWith('/monitor/data')) return t('nav.monitorData')
    if (path.startsWith('/monitor')) return t('nav.monitorManage')
    if (path.startsWith('/analysis')) return t('nav.analysis')
    if (path.startsWith('/volume')) return t('nav.volume')
    if (path.startsWith('/publicoption') && path !== '/publicoption') return t('publicOption.reportDetail')
    if (path.startsWith('/publicoption')) return t('nav.publicOption')
    if (path.startsWith('/report') && path !== '/report') return t('report.viewDetail')
    if (path.startsWith('/report')) return t('nav.report')
    if (path.startsWith('/settings/warning-msg')) return t('nav.settingsWarningMsg')
    if (path.startsWith('/settings/warning')) return t('nav.settingsWarning')
    if (path.startsWith('/settings/users')) return t('nav.settingsUsers') || '用户管理'
    if (path.startsWith('/settings/preference')) return t('nav.settingsPreference')
    if (path.startsWith('/settings/feedback')) return t('nav.settingsFeedback')
    if (path.startsWith('/settings')) return t('nav.settings')
    return t('common.appName')
  }

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-6">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">{getPageTitle()}</h1>
      </div>

      <div className="flex items-center gap-4">
        {/* Warning Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" onClick={() => navigate({ to: '/settings/warning-msg' })}>
              <Bell className="h-5 w-5" />
              {warningCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -right-1 -top-1 h-5 min-w-[20px] justify-center px-1 text-xs"
                >
                  {warningCount > 99 ? '99+' : warningCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>{t('nav.settingsWarningMsg')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {warningCount > 0 ? (
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate({ to: '/settings/warning-msg' })}>
                {t('settings.warning.messageList')} ({warningCount})
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem className="text-muted-foreground">
                {t('common.noData')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>{userName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={() => navigate({ to: '/settings/preference' })}>
              <Settings className="mr-2 h-4 w-4" />
              {t('nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer text-destructive" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('login.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
